import React from "react";
import withStyles from "@material-ui/core/styles/withStyles";
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Globales from "utils/Globales";

const styles = {
	checkoutContainer: {
		width: "100%",
		minHeight: "600px",
		position: "relative",
	},
	iframe: {
		width: "100%",
		minHeight: "600px",
		border: "none",
		borderRadius: "8px",
	},
	loadingContainer: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		minHeight: "600px",
		padding: "40px",
	},
	errorContainer: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		minHeight: "400px",
		padding: "40px",
		textAlign: "center",
	},
};

class CheckoutEmbedded extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			scriptLoaded: false,
			checkoutInitialized: false,
			error: null,
		};
		this.containerRef = React.createRef();
		this.checkoutInstance = null;
		this.checkInterval = null;
	}

	componentDidMount() {
		this.loadConektaCheckoutScript();
	}

	componentWillUnmount() {
		// Limpiar intervalo de verificación
		if (this.checkInterval) {
			clearInterval(this.checkInterval);
		}

		// Limpiar instancia de checkout si existe
		if (this.checkoutInstance) {
			try {
				// Conekta puede tener un método de limpieza
				if (this.checkoutInstance.destroy) {
					this.checkoutInstance.destroy();
				}
			} catch (error) {
				// Error al limpiar checkout (no crítico)
			}
		}
	}

	loadConektaCheckoutScript = () => {
		// Verificar si el script ya está cargado
		if (window.ConektaCheckoutComponents) {
			this.setState({ scriptLoaded: true });
			// Pequeño delay para asegurar que el DOM esté listo
			setTimeout(() => {
				this.initializeCheckout();
			}, 100);
			return;
		}

		// Verificar si el script ya está siendo cargado
		const existingScript = document.querySelector(
			'script[src="https://pay.conekta.com/v1.0/js/conekta-checkout.min.js"]'
		);
		if (existingScript) {
			existingScript.addEventListener("load", () => {
				this.setState({ scriptLoaded: true });
				setTimeout(() => {
					this.initializeCheckout();
				}, 100);
			});
			return;
		}

		// Cargar el script de Conekta Checkout Components
		const script = document.createElement("script");
		script.src = "https://pay.conekta.com/v1.0/js/conekta-checkout.min.js";
		script.async = true;
		script.onload = () => {
			this.setState({ scriptLoaded: true });
			// Pequeño delay para asegurar que el DOM esté listo
			setTimeout(() => {
				this.initializeCheckout();
			}, 100);
		};
		script.onerror = (error) => {
			this.handleError("Error al cargar Conekta Checkout");
		};
		document.body.appendChild(script);
	};

	initializeCheckout = () => {
		if (!window.ConektaCheckoutComponents) {
			this.handleError("Conekta Checkout Components no está disponible");
			return;
		}

		if (!this.props.checkoutRequestId) {
			this.handleError("ID de checkout no proporcionado");
			return;
		}

		// Asegurar que el contenedor existe
		if (!this.containerRef.current) {
			this.handleError("Contenedor de checkout no encontrado");
			return;
		}

		// Verificar que el elemento existe en el DOM
		const containerElement = document.getElementById("conektaIframeContainer");
		if (!containerElement) {
			this.handleError("Contenedor de checkout no encontrado en el DOM");
			return;
		}

		// Asegurar que el contenedor esté visible ANTES de inicializar
		// Esto es crítico - Conekta necesita que el contenedor sea visible
		containerElement.style.display = "block";
		containerElement.style.visibility = "visible";
		containerElement.style.opacity = "1";
		containerElement.style.height = "700px";
		containerElement.style.minHeight = "700px";
		containerElement.style.width = "100%";

		// Verificar que la clave pública existe
		const publicKey = import.meta.env.VITE_PUBLIC_CONEKTA_KEY;
		if (!publicKey) {
			this.handleError("Clave pública de Conekta no configurada");
			return;
		}

		// Esperar un momento para asegurar que el DOM esté completamente renderizado
		// y que el contenedor sea visible antes de inicializar
		setTimeout(() => {
			this.doInitializeCheckout(containerElement, publicKey);
		}, 200);
	};

	doInitializeCheckout = (containerElement, publicKey) => {
		try {
			// Validar que checkoutRequestId existe y tiene formato correcto
			if (!this.props.checkoutRequestId) {
				this.handleError("ID de checkout no proporcionado");
				return;
			}

			// Usar selector string como en el ejemplo de Conekta (evita error de estructura circular)
			const integrationConfig = {
				targetIFrame: "#conektaIframeContainer", // Usar selector string, no elemento DOM
				checkoutRequestId: this.props.checkoutRequestId,
				publicKey: publicKey,
				options: {},
				styles: {},
				onFinalizePayment: (event) => {
					this.handlePaymentFinalized(event);
				},
				onError: (error) => {
					this.handleError(
						error.message || error.error || "Error en el checkout de Conekta"
					);
				},
			};

			// Agregar onReady si está disponible (puede que no todas las versiones lo tengan)
			if (window.ConektaCheckoutComponents.Integration.prototype?.onReady) {
				integrationConfig.onReady = () => {
					this.setState({ checkoutInitialized: true });
				};
			}

			// Intentar inicializar el checkout
			try {
				this.checkoutInstance = window.ConektaCheckoutComponents.Integration(
					integrationConfig
				);
			} catch (integrationError) {
				throw integrationError;
			}

			// Agregar listener para errores del iframe después de que se cree
			setTimeout(() => {
				const iframe = containerElement?.querySelector("iframe");
				if (iframe) {
					iframe.onload = () => {
						this.setState({ checkoutInitialized: true });
					};
				}
			}, 500);

			// Verificar periódicamente si el iframe se ha creado
			let checkCount = 0;
			const maxChecks = 10; // Verificar por hasta 10 segundos
			this.checkInterval = setInterval(() => {
				checkCount++;
				const iframe = containerElement?.querySelector("iframe");

				if (iframe) {
					// Si el iframe no tiene src después de varios intentos, puede haber un problema
					if (!iframe.src || iframe.src.length === 0) {
						// Intentar forzar la carga del iframe si tiene un método load
						if (checkCount === 5 && iframe && !iframe.src) {
							// Algunas veces el iframe necesita ser forzado a cargar
							try {
								if (iframe.contentWindow) {
									// Iframe tiene contentWindow
								}
							} catch (e) {
								// No se puede acceder a contentWindow (normal por CORS)
							}
						}
					}

					// Verificar si el iframe tiene contenido
					if (iframe.src && iframe.src.length > 0) {
						clearInterval(this.checkInterval);
						this.checkInterval = null;
						this.setState({ checkoutInitialized: true });
					} else if (checkCount >= maxChecks) {
						// Después de maxChecks, mostrar el contenedor de todas formas
						clearInterval(this.checkInterval);
						this.checkInterval = null;
						this.setState({ checkoutInitialized: true });
					}
				} else if (checkCount >= maxChecks) {
					clearInterval(this.checkInterval);
					this.checkInterval = null;
					// Mostrar el contenedor de todas formas
					this.setState({ checkoutInitialized: true });
				}
			}, 1000);
		} catch (error) {
			this.handleError(`Error al inicializar el checkout: ${error.message}`);
		}
	};

	handlePaymentFinalized = (event) => {
		// El evento puede tener información sobre el resultado del pago

		// Verificar diferentes formatos de respuesta de Conekta
		const status = event.status || event.payment_status || event.state;
		const isPaid =
			status === "paid" ||
			status === "success" ||
			status === "completed" ||
			event.paid === true;

		if (isPaid) {
			this.handleSuccess({ status, pending: false });
		} else if (
			status === "error" ||
			status === "failed" ||
			status === "declined"
		) {
			const errorMessage =
				event.message ||
				event.error_message ||
				event.error ||
				"Error al procesar el pago";
			this.handleError(errorMessage);
		} else {
			// SPEI/OXXO u otro método asíncrono: el checkout ya se generó
			this.handleSuccess({ status, pending: true });
		}
	};

	handleSuccess = (paymentInfo = {}) => {
		if (this.props.onSuccess) {
			this.props.onSuccess(this.props.orderId, paymentInfo);
		}
	};

	handleError = (message) => {
		this.setState({ error: message });
		if (this.props.onError) {
			this.props.onError(message);
		}
	};

	render() {
		const { classes, checkoutRequestId, orderId } = this.props;
		const { scriptLoaded, checkoutInitialized, error } = this.state;

		if (error) {
			return (
				<Card>
					<CardBody>
						<div className={classes.errorContainer}>
							<Typography variant="h6" color="error" gutterBottom>
								Error al procesar el pago
							</Typography>
							<Typography variant="body2" color="textSecondary">
								{error}
							</Typography>
						</div>
					</CardBody>
				</Card>
			);
		}

		return (
			<Card>
				<CardBody>
					<GridContainer>
						<GridItem xs={12}>
							<div className={classes.checkoutContainer}>
								{(!scriptLoaded || !checkoutInitialized) && (
									<div className={classes.loadingContainer}>
										<CircularProgress />
										<Box mt={2}>
											<Typography variant="body2" color="textSecondary">
												{!scriptLoaded
													? "Cargando Conekta Checkout..."
													: "Inicializando checkout..."}
											</Typography>
										</Box>
									</div>
								)}
								<div
									id="conektaIframeContainer"
									ref={this.containerRef}
									style={{
										width: "100%",
										minHeight: "700px",
										height: "700px",
										display: checkoutInitialized ? "block" : "none",
										visibility: checkoutInitialized ? "visible" : "hidden",
										opacity: checkoutInitialized ? 1 : 0,
										position: "relative",
									}}
								/>
							</div>
						</GridItem>
					</GridContainer>
				</CardBody>
			</Card>
		);
	}
}

export default withStyles(styles)(CheckoutEmbedded);
